// Static catalogs mirror the backend SERVICES_CATALOG / TEAM_CATALOG / TESTIMONIALS
// Used as instant render data; pages can re-fetch from /api/* if needed.
import {
    ShieldCheck, Receipt, Lightbulb, BookOpen, Building2,
    Globe2, FileCheck, Briefcase, LineChart, Headphones, Laptop, Landmark,
} from "lucide-react";

export const ICONS = {
    ShieldCheck, Receipt, Lightbulb, BookOpen, Building2,
    Globe2, FileCheck, Briefcase, LineChart, Headphones, Laptop, Landmark,
};

export const SERVICES = {
    india: [
        { slug: "audit-assurance", title: "Audit & Assurance", icon: "ShieldCheck",
          summary: "Thorough audits that enhance the credibility of your financial statements.",
          bullets: ["Statutory & Internal Audits", "Financial Statement Audits", "Compliance & Internal Controls Reviews", "Risk Assessment"] },
        { slug: "taxation", title: "Taxation Advisory Services", icon: "Receipt",
          summary: "Strategic tax planning and compliance advisory for US businesses.",
          bullets: ["Tax Planning & Strategy", "Tax Return Preparation", "Compliance & Filings", "Tax Dispute Resolution"] },
        { slug: "business-advisory", title: "Business Advisory", icon: "Lightbulb",
          summary: "Data-driven insights and strategic guidance for informed decisions.",
          bullets: ["Financial Planning & Forecasting", "Business Valuation", "MIS & Dashboard Reporting", "Fundraising & Investment Advisory"] },
        { slug: "accounting-bookkeeping", title: "Accounting & Bookkeeping", icon: "BookOpen",
          summary: "Meticulous, technology-driven bookkeeping that stays accurate and current.",
          bullets: ["Bookkeeping", "Financial Statement Preparation", "Accounts Receivable & Payable", "Payroll Processing"] },
        { slug: "company-setup", title: "Business Setup / Entity Registration", icon: "Building2",
          summary: "End-to-end entity registration and regulatory onboarding for new businesses.",
          bullets: ["Business Setup / Entity Registration", "Taxation Advisory Services", "Compliance & Regulatory Filings", "Operating License Support"] },
    ],
    international: [
        { slug: "intl-accounting", title: "Accounting & Bookkeeping", icon: "Globe2",
          summary: "Comprehensive accounting aligned to international standards.",
          bullets: ["Accounts payable / receivable management", "Bank reconciliations", "Financial reporting & statements", "Payroll support with EU awareness"],
          idealFor: "Accounting firms, SMEs, startups" },
        { slug: "tax-compliance-eu", title: "Tax & Compliance Support", icon: "FileCheck",
          summary: "Expert assistance for tax prep and compliance across Europe.",
          bullets: ["VAT return preparation (Netherlands & EU)", "Corporate tax support", "Documentation & reporting", "Cross-border compliance"],
          idealFor: "EU businesses, international companies" },
        { slug: "business-support", title: "Business Support Services", icon: "Briefcase",
          summary: "Reliable administrative support to free up your team.",
          bullets: ["Data entry & processing", "Virtual assistance", "Email & calendar management", "Document management"],
          idealFor: "Consultants, small businesses, entrepreneurs" },
        { slug: "financial-analysis", title: "Financial Analysis & Reporting", icon: "LineChart",
          summary: "Strategic insights that drive informed business decisions.",
          bullets: ["MIS reports & dashboards", "Forecasting & budgeting", "KPI tracking & analysis", "Management reporting"],
          idealFor: "Growing businesses, investors, management teams" },
        { slug: "customer-support", title: "Customer Support Services", icon: "Headphones",
          summary: "Professional support that boosts satisfaction and retention.",
          bullets: ["Email & chat support", "Back-office support", "Customer query resolution", "Multilingual support available"],
          idealFor: "E-commerce, SaaS companies, service providers" },
        { slug: "it-digital", title: "IT & Digital Support", icon: "Laptop",
          summary: "Technical support to keep your digital infrastructure healthy.",
          bullets: ["Website maintenance", "Basic development support", "CRM management", "System administration"],
          idealFor: "Tech startups, digital agencies, online businesses" },
        { slug: "eu-specialized", title: "Specialized EU Support", icon: "Landmark",
          summary: "Tailored services for businesses operating in European markets.",
          bullets: ["EU compliance expertise", "Cross-border documentation", "GDPR support", "Netherlands market specialization"],
          idealFor: "EU-based companies, businesses expanding to Europe" },
    ],
};

export const ALL_SERVICE_TITLES = [
    ...SERVICES.india.map(s => s.title),
    ...SERVICES.international.map(s => `${s.title} (International)`),
];

// Two-track service typology surfaced on the Services page.
// "us"  → Accounting & Bookkeeping suite for US clients (5 services, sourced from SERVICES.india)
// "eu"  → Build Your Remote Team suite for EU clients (6 services, sourced from SERVICES.international minus intl-accounting)
export const SERVICE_TYPES = [
    {
        id: "us",
        kicker: "Track 1",
        title: "Accounting & Bookkeeping",
        audience: "For US clients",
        summary: "Audit, taxation, advisory, daily bookkeeping and company setup — delivered to US standards, with senior CA review.",
        services: SERVICES.india,
    },
    {
        id: "eu",
        kicker: "Track 2",
        title: "Build Your Remote Team",
        audience: "For EU clients",
        summary: "An embedded back-office team — compliance, financial analysis, customer support and IT — calibrated for European businesses and GDPR.",
        services: SERVICES.international.filter(s => s.slug !== "intl-accounting"),
    },
];

// Generic dual-avatar illustration kept for potential future use by
// renderers needing a fallback. Currently the only members in TEAM have
// real headshots, so this is not assigned to any entry.
export const TEAM_AVATAR = "https://customer-assets.emergentagent.com/job_bookkeeping-pro-30/artifacts/f3w12xbb_image.png";
const GOPICHAND_PHOTO = "https://customer-assets.emergentagent.com/job_bookkeeping-pro-30/artifacts/kng0rswl_Gemini_Generated_Image_ew35feew35feew35%20%282%29.png";

export const TEAM = [
    {
        slug: "gopichand-a",
        name: "CA Gopichand A",
        role: "Founder & Partner",
        gender: "male",
        bio: "Chartered Accountant (ICAI, May 2019) and founder of MCA & Associates (India) and VITTANITI LLP (US). Big-4 audit background — PwC and KPMG — channelled into a partner-led, precision-first practice.",
        linkedin: "https://www.linkedin.com/in/ca-gopichand-b1b567192/",
        photo: GOPICHAND_PHOTO,
        detail: {
            blurbs: [
                "CA Gopichand A is a qualified Chartered Accountant (ICAI, May 2019) and the founder of two accounting entities — MCA & Associates serving Indian clients and VITTANITI LLP, a dedicated US accounting entity serving American businesses and CPA firms. With formative years in global audit at PwC and KPMG, Gopichand brings Big-4 discipline and precision to a practice built for personalised, partner-led service.",
                "Through VITTANITI LLP, US clients gain access to a committed offshore accounting partner that delivers bookkeeping, financial reporting and US GAAP advisory with the professionalism expected from a domestic firm. Over 3+ years of dedicated US client service, the team has developed deep familiarity with American accounting workflows, software ecosystems and compliance standards — making the transition seamless for any business or CPA firm looking to outsource with confidence.",
                "The culture across both entities is anchored in three core principles: accuracy in every number, transparency in every client relationship and timeliness in every deliverable. Gopichand personally oversees engagements, ensuring advice reflects both deep technical expertise and a genuine understanding of each client's business context.",
                "The team's commitment to continuous learning — staying current with evolving US GAAP standards, Indian regulatory changes and global best practices — makes this dual-entity structure a trusted, cost-effective solution for US businesses seeking international-grade accounting support.",
            ],
            timeline: [
                { label: "Qualified CA — ICAI", meta: "May 2019 · Institute of Chartered Accountants of India" },
                { label: "Audit Professional — PwC Global", meta: "Global Audit Practice" },
                { label: "Audit Professional — KPMG Global", meta: "Global Audit Practice" },
                { label: "Founder & Partner — MCA & Associates", meta: "2023 – Present · Hyderabad, India · Indian Practice" },
                { label: "Founder — VITTANITI LLP", meta: "US-Focused Accounting Entity · Serving American Businesses & CPA Firms" },
            ],
            stats: [
                { value: "7+ yrs", label: "Experience" },
                { value: "3+ years", label: "of US GAAP experience" },
                { value: "2", label: "Big-4 Firms" },
                { value: "2", label: "Entities" },
            ],
            entities: [
                {
                    name: "MCA & Associates",
                    region: "Indian Practice",
                    body: "Hyderabad-based CA firm serving Indian businesses with full-spectrum accounting, tax and compliance services.",
                    tags: ["GST & Income Tax", "Companies Act", "Indian Audit"],
                },
                {
                    name: "VITTANITI LLP",
                    region: "US-Focused Entity",
                    body: "Dedicated offshore accounting partner for US businesses and CPA firms — bookkeeping, reporting and GAAP advisory.",
                    tags: ["US Bookkeeping", "US GAAP Advisory", "Financial Reporting"],
                },
            ],
            services: [
                { title: "Indian Accounting & Tax", body: "GST, Income Tax and Companies Act compliance." },
                { title: "US Bookkeeping", body: "Accurate books, reconciliations and financial reporting." },
                { title: "US GAAP Advisory", body: "ASC 606 revenue recognition, ASC 842 leases and adjacent standards." },
                { title: "Audit Advisory", body: "Backed by PwC & KPMG global audit experience." },
            ],
        },
    },
    { slug: "pranoy", name: "Pranoy", role: "Remote Team Lead", gender: "male",
      bio: "Holds over a decade of experience working with Big 4s and international teams within Europe and the UK.",
      linkedin: "https://www.linkedin.com/",
      photo: "https://customer-assets.emergentagent.com/job_bookkeeping-pro-30/artifacts/lf4clfki_WhatsApp%20Image%202026-06-21%20at%205.54.41%20PM.jpeg" },
];

export const TESTIMONIALS = [
    { name: "Athena Tech Inc" },
    { name: "Cloudtree Sys LLC" },
    { name: "SP Sanford LLC" },
    { name: "Legend Liquors" },
    { name: "SS Casselberry" },
    { name: "Techsemble Inc" },
    { name: "Nvision Tek Inc" },
    { name: "Nvision Group Inc" },
    { name: "Infolance Inc" },
    { name: "Nextgen Software Solutions LLC" },
    { name: "Virtuoso Technologies Inc" },
    { name: "EZ2 Technologies Inc" },
    { name: "SAK Infotech" },
];

export const STATS = [
    { value: "60%", label: "Cost Reduction" },
    { value: "4+", label: "Countries Served" },
    { value: "100%", label: "GDPR Compliant" },
    { value: "5yr+", label: "Industry Experience" },
];

export const MILESTONES = [
    { year: "2015", title: "Founded in Hyderabad",
      detail: "Vittaniti opens its first office in Banjara Hills with a 3-person audit team." },
    { year: "2018", title: "Crossed 100 retainer clients",
      detail: "Expanded into GST advisory and payroll services as demand surged post-GST rollout." },
    { year: "2021", title: "Launched EU desk",
      detail: "Set up a Netherlands liaison to support VAT and GDPR for cross-border clients." },
    { year: "2023", title: "Cloud-first bookkeeping",
      detail: "Migrated 80% of clients to real-time, dashboard-driven monthly closes." },
    { year: "2025", title: "12 service lines, one trusted firm",
      detail: "Today Vittaniti serves founders, SMEs and listed entities across India and Europe." },
];

export const HYDERABAD_ADDRESS = {
    line1: "3rd Floor, Pearl Heights",
    line2: "Road No. 12, Banjara Hills",
    city: "Hyderabad, Telangana 500034",
    country: "India",
    phone: "+91 97055 52221",
    whatsapp: "https://wa.me/919705552221?text=Hi%20Vittaniti%2C%20I%27d%20like%20to%20learn%20more%20about%20your%20services.",
    email: "info@vittaniti.com",
    hours: "Mon – Fri · 9:30 AM to 6:30 PM IST",
    mapsEmbed: "https://www.google.com/maps?q=Banjara+Hills+Hyderabad&output=embed",
};

export const BRAND = {
    name: "Vittaniti",
    full: "Vittaniti Shared Solutions",
    logo: "https://customer-assets.emergentagent.com/job_bookkeeping-pro-30/artifacts/na6cdx8t_image.png",
    tagline: "Your Extended Office, From India",
};

export const IMAGES = {
    hero: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1800&q=85&auto=format&fit=crop",
    hyderabad: "https://images.unsplash.com/photo-1696941515998-d83f24967aca?w=1400&q=80&auto=format&fit=crop",
    skyline: "https://images.unsplash.com/photo-1758431145201-2846e60054b0?w=1400&q=80&auto=format&fit=crop",
    teamWide: "https://images.unsplash.com/photo-1758691737060-3814f16d5aba?w=1600&q=80&auto=format&fit=crop",
    deskFocus: "https://images.unsplash.com/photo-1758876022088-2d46af5635c2?w=1200&q=80&auto=format&fit=crop",
    accountingDesk: "https://images.unsplash.com/photo-1762427355235-dd22e5cb010c?w=1200&q=80&auto=format&fit=crop",
};
